import React, { forwardRef, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import { Form } from "antd";
import { forEach, isEmpty, uniqBy, filter } from 'lodash';

const TraceableMappingFormV2 = forwardRef(function TraceableMappingFormV2(props, ref) {
    const IntRef = useRef({});
    const serviceRef = useRef({})
    const [form] = Form.useForm();
    const [mappedEnvServiceList] = useState(() => {
        if (props.record && !isEmpty(props.record.serviceId)) {
            const { serviceId, serviceName, environmentId, integrationType } = props.record;
            const serviceIdArray = serviceId.split(',');
            const serviceNameArray = serviceName.split(',');
            return {
                service: serviceIdArray.map((id, index) => ({ id, name: serviceNameArray[index], otherProperties: { type: integrationType } })),
                environment: [ { id: environmentId, name: environmentId}]
            }
        }
        return { environment: [], service: [] };
    });

    const fetchProjects = useCallback((params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
        return {
            promise: promise.then(response => {
                let { projects } = response;
                // merging the already mapped environment/(service/domain) to the fetched list so that if the selected value is not in the option list
                // then id should not be displayed
                if (!isEmpty(mappedEnvServiceList.environment) && !isEmpty(projects) && isEmpty(params.type) && isEmpty(params.organisation)) {
                    projects = uniqBy([...projects, ...mappedEnvServiceList.environment], 'id');
                }
                if (!isEmpty(mappedEnvServiceList.service) && !isEmpty(projects) && !isEmpty(params.type) && !isEmpty(params.organisation)) {
                    projects = filter(uniqBy([...projects, ...mappedEnvServiceList.service], 'id'), item => item.otherProperties.type === params.type);
                }
                if (!isEmpty(params.type) && !isEmpty(params.organisation)) {
                    forEach(projects, p => {
                        serviceRef.current[p.id] = p.name;
                    });
                }
                return { ...response, projects };
            }),
            cancel
        }
    }, [props.loginConfigId, props.toolName, mappedEnvServiceList])


    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { serviceId } = config;
        return ({
            serviceId: serviceId?.split(',')
        });
    }, []);

    const getExtraFieldsData = useCallback((config) => {
        const { environmentId, integrationType, serviceId } = config;

        const serviceName = isEmpty(serviceId) ? undefined : serviceId.map((id) =>  serviceRef.current[id]);
        return ({
            environmentId,
            integrationType,
            serviceId: isEmpty(serviceId) ? undefined : serviceId.join(','),
            serviceName: isEmpty(serviceName) ? undefined : serviceName.join(','),
            //send the property received in config
            ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        });
    }, [])

    const onValuesChange = useCallback((value, field) => {
        const { name } = field || {};
        const configurations = form.getFieldValue("configurations");
        let updatedConfiguration = [
            ...configurations,
        ];
        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            serviceId: undefined,
        }
        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form]);

    const extraFields = useMemo(() => ([
        {
            name: 'environmentId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Environment',
            required: true,
            props: {
                api: fetchProjects,
                onChange: onValuesChange,
                autoFocus: true
            }
        },
        {
            colSpan: 12,
            type: FieldTypes.LABEL
        },
        {
            name: 'integrationType',
            colSpan: 12,
            type: FieldTypes.SELECT,
            label: 'Integration Type',
            options: [{ label: 'Domain', value: 'domain' }, { label: 'Service', value: 'service' }],
            required: true,
            props: {
                onChange: onValuesChange
            }
        },
        {
            name: 'serviceId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Service/Domain Name',
            props: {
                setExtraProps: (fieldProps, currentField) => {
                    const configurations = form.getFieldValue("configurations");

                    const currentConfiguration = configurations[currentField.name] || {};
                    const environmentId = currentConfiguration?.environmentId;
                    const integrationType = currentConfiguration?.integrationType;
                    const keyName = `${environmentId}_${integrationType}`;
                    if (environmentId && integrationType && !IntRef.current[keyName]) {
                        IntRef.current[keyName] = { organisation: environmentId, type : integrationType}
                    }
                
                    return {
                        mode: "multiple",
                        api: environmentId && integrationType && fetchProjects,
                        allowClear: true,
                        apiParams: IntRef.current[keyName],
                        resetOptionsProp: IntRef.current[keyName],
                    }
                }
            }
        }
    ]), [fetchProjects, form, onValuesChange]);
    return (
        <GenericMappingForm
            ref={ref}
            {...props}
            form={form}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        />
    )
})

export default TraceableMappingFormV2;